import { createQuery } from '@shared/lib/query-helpers';

import { z } from 'zod/v4';

import { userProfileContextResponseSchema, userProfileIdentityResponseSchema } from './schemas';

export const userProfileKeys = {
  all: ['user-profile'] as const,
  actor: (accountId: string) => [...userProfileKeys.all, { accountId }] as const,
  identity: (accountId: string) => [...userProfileKeys.actor(accountId), 'identity'] as const,
  context: (accountId: string, profileRef = 'me') =>
    [...userProfileKeys.actor(accountId), { profileRef }, 'context'] as const,
};

/** Resolve only the authenticated account's profile; an identity hint never grants resource access. */
export const userProfileIdentityQuery = (accountId: string) =>
  createQuery({
    queryKey: userProfileKeys.identity(accountId),
    path: '/api/v1/user-profiles/resolve',
    requestOptions: { params: { account_id: accountId } },
    schema: userProfileIdentityResponseSchema.refine(
      (response) => response.data.accountId === accountId,
      {
        message: 'Resolved profile does not belong to the authenticated account',
        path: ['data', 'accountId'],
      }
    ),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: true,
    retry: false,
    enabled: z.uuid().safeParse(accountId).success,
  });

export const userProfileContextQuery = (accountId: string, profileRef = 'me', enabled = true) =>
  createQuery({
    queryKey: userProfileKeys.context(accountId, profileRef),
    path: `/api/v1/user-profiles/${encodeURIComponent(profileRef)}/context`,
    schema: userProfileContextResponseSchema,
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
    refetchOnWindowFocus: true,
    enabled: enabled && accountId.length > 0 && profileRef.length > 0,
  });
